import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, RefreshCw, Trash2, Upload, Wrench } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getErrorMessage } from "../../api/client";
import { avvisoLettura } from "../../utils/lettureOdoo";
import * as interventionsApi from "../../api/interventions";
import { Intervento } from "../../api/interventions";
import * as anagraficaApi from "../../api/anagrafica";
import { CentroAssistenzaOption } from "../../api/anagrafica";
import { sectionFlatStyle, sectionTileStyle } from "../../utils/sectionColors";
import { Table, TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { SlideOver } from "../../components/ui/SlideOver";
import {
  RiferimentiOdoo,
  ValoriRiferimentiOdoo,
  RIFERIMENTI_ODOO_VUOTI,
  payloadRiferimentiOdoo,
  usaPuoGestireRiferimentiOdoo,
} from "../../components/ui/RiferimentiOdoo";
import { MediaUploader } from "../../components/ui/MediaUploader";
import { ImportModal } from "../../components/ui/ImportModal";
import { ExportButton } from "../../components/ui/ExportButton";
import { Modal } from "../../components/ui/Modal";
import { RapportinoRIT } from "../../components/ui/RapportinoRIT";
import { InterventoModifica } from "../../components/ui/InterventoModifica";
import { CampiOrario } from "../../components/ui/CampiOrario";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";
import { SedeAutocomplete } from "../../components/ui/SedeAutocomplete";
import { DeviceFisicoAutocomplete } from "../../components/ui/DeviceFisicoAutocomplete";
import { VigilanzaCongiunta, payloadVigilanza } from "../../components/ui/VigilanzaCongiunta";
import { ScheduleAutocomplete } from "../../components/ui/ScheduleAutocomplete";
import { DispositivoAutocomplete } from "../../components/ui/DispositivoAutocomplete";
import { DispositivoMateriale } from "../../components/ui/DispositivoMateriale";
import { MeseSelect } from "../../components/ui/MeseSelect";
import { RitardoBanner } from "../../components/ui/RitardoBanner";
import { StatoFilterBanner } from "../../components/ui/StatoFilterBanner";
import { coloreFetta } from "../../components/ui/DonutPerStato";
import { useMessaggioListaVuota } from "../../components/ui/ListaVuota";
import { meseDaUrl, useOrdinamentoUrl, usePannelloRecord, useRicercaUrl, useScriviParametri } from "../../hooks/useStatoUrl";

/**
 * Il testo della sezione OdV nel riquadro del pannello. Solo le parole: la
 * sezione arriva calcolata dal backend, con i nomi delle sezioni del Report
 * Ordini. `null` con un numero OdV vuol dire già fatturato.
 */
function testoSezioneOdv(s: Intervento["sezione_odv"]): string {
  if (!s) return "—";
  const testo =
    s.sezione === "da_fatturare"
      ? "da fatturare"
      : s.sezione === "non_ancora_fatturabili"
        ? "non ancora fatturabile"
        : s.sezione === "non_da_fatturare"
          ? "non da fatturare: in garanzia o annullato"
          : s.sezione === "da_qualificare"
            ? "da qualificare"
            : "fatturato";
  return s.motivo ? `${testo} — ${s.motivo}` : testo;
}

export default function Interventions() {
  const { user, hasPermission } = useAuth();
  const { showToast } = useToast();

  const canSync = user?.role === "admin" || user?.role === "staff" || user?.role === "centro_assistenza";
  // Creazione manuale: stessi ruoli della sync — un CA crea per sé, admin/staff possono anche assegnare ad altri.
  const canCreate = canSync;
  const isAdminOrStaff = user?.role === "admin" || user?.role === "staff";
  // Import massivo: solo admin/staff, stessi ruoli di IMPORT_ROLES nel backend
  // (routes/import.ts) — un CA vedrebbe altrimenti un bottone che dà 403.
  const canImport = isAdminOrStaff;
  // Ruolo cliente (Fase 3.3): sola consultazione — nasconde le sezioni di
  // scrittura del dettaglio (nota Odoo, R.I.T., upload media).
  const isReadOnly = user?.role === "cliente";
  // Modifica dei dati operativi: segue il permesso, non il ruolo, così valgono
  // anche gli override per-utente (Fase 2.4).
  const canUpdate = hasPermission("interventi:update");

  const [searchParams, setSearchParams] = useSearchParams();
  const ritardo = searchParams.get("ritardo") === "true";
  // Drill-down per stato dal donut della Dashboard. Filtrato lato server come
  // mese e ritardo, non sui record già in pagina: così il filtro vale su tutto
  // l'insieme visibile al ruolo e non solo sulla fetta già caricata.
  const statoFiltro = searchParams.get("stato") ?? "";
  // In pagina si mostra l'etichetta leggibile, la stessa usata dal donut.
  const etichettaStato = statoFiltro === interventionsApi.STATO_NULLI ? "Senza stato" : statoFiltro;

  function rimuoviParam(nome: string) {
    const next = new URLSearchParams(searchParams);
    next.delete(nome);
    setSearchParams(next);
  }

  const [records, setRecords] = useState<Intervento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Mese, ricerca, ordinamento e intervento aperto stanno nell'URL come
  // `ritardo` e `stato` (vedi `hooks/useStatoUrl.ts`): tornando indietro, o
  // aprendo un link, si ritrova la stessa vista.
  const scrivi = useScriviParametri();
  const { ordine, onOrdineChange } = useOrdinamentoUrl();
  const mese = meseDaUrl(searchParams);
  const setMese = (m: number | undefined) => scrivi({ mese: m ? String(m) : null });
  // Con un filtro attivo il vuoto è spiegato dal filtro, non dall'assegnazione.
  const messaggioVuoto = useMessaggioListaVuota(
    "Nessun intervento.",
    Boolean(statoFiltro || ritardo || mese !== undefined)
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [selected, setSelected] = useState<Intervento | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [centri, setCentri] = useState<CentroAssistenzaOption[]>([]);

  const { testo: ricerca, setTesto: setRicerca, valore: ricercaAttiva } = useRicercaUrl();

  async function loadRecords() {
    setIsLoading(true);
    try {
      // mese/anno, ritardo e stato filtrati lato server.
      const { records: list } = await interventionsApi.listInterventi({
        mese,
        ritardo: ritardo || undefined,
        stato: statoFiltro || undefined,
        search: ricercaAttiva || undefined,
      });
      setRecords(list);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile caricare gli interventi."), "error");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mese, ritardo, statoFiltro, ricercaAttiva]);

  // Pannello di dettaglio su `?open=<id>`, che arrivi dal clic su una riga o
  // da un link (Calendario unificato, Report ordini, scheda cliente); modulo
  // di creazione su `?new=1`, anche dall'azione rapida della Dashboard.
  const pannello = usePannelloRecord({
    records,
    isLoading,
    preparaNuovo: () => undefined,
    preparaModifica: setSelected,
  });
  const panelOpen = pannello.apertoModifica;
  const createOpen = pannello.apertoNuovo;

  useEffect(() => {
    if (isAdminOrStaff) {
      anagraficaApi.listCentriAssistenza().then(({ centri }) => setCentri(centri));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminOrStaff]);

  async function handleSync() {
    setIsSyncing(true);
    try {
      const {
        synced,
        discrepanze,
        helpdesk,
        field_service,
        collegati_cliente,
        collegati_sede,
        collegamenti_incoerenti,
        lettura,
      } = await interventionsApi.syncInterventi();
      const avviso = avvisoLettura(lettura);
      const collegati = collegati_cliente + collegati_sede;
      showToast(
        (avviso ? `${avviso.testo} · ` : "") +
          `Sincronizzati ${synced} (${helpdesk} Helpdesk, ${field_service} Assistenza sul campo)` +
          (discrepanze > 0 ? `, ${discrepanze} campi aggiornati rispetto all'ultimo sync.` : ", nessuna discrepanza.") +
          // Dichiarato solo quando e' successo qualcosa: a regime questo numero
          // e' zero a ogni sync, perche' i collegamenti si scrivono una volta
          // sola e poi restano. Uno zero stampato ogni volta si leggerebbe come
          // un guasto invece che come "non c'era altro da collegare".
          (collegati > 0
            ? ` · ${collegati_sede} sedi e ${collegati_cliente} clienti collegati all'anagrafica`
            : "") +
          // Dice cosa fare, non solo cosa e' successo. Chi legge questo toast e'
          // un admin, non chi ha scritto la sync: "l'azienda madre non
          // corrisponde" da solo e' un fatto neutro, e un numero senza un
          // verbo si legge come una statistica invece che come lavoro da fare.
          (collegamenti_incoerenti > 0
            ? ` · ${collegamenti_incoerenti === 1 ? "1 sede da verificare a mano" : `${collegamenti_incoerenti} sedi da verificare a mano`}: il cliente del ticket Odoo non e' quello gia' collegato all'intervento, quindi la sede non e' stata scritta`
            : ""),
        avviso || collegamenti_incoerenti > 0 ? "warning" : "success",
        // Il toast resta finche' non lo si chiude, come per le password
        // temporanee: e' una segnalazione da leggere e su cui agire, e tre
        // secondi bastano appena ad accorgersi che c'era scritto qualcosa.
        // Solo nel caso anomalo — il messaggio normale continua a sparire da
        // solo, altrimenti ogni sync lascerebbe una riga da chiudere a mano.
        { persistent: collegamenti_incoerenti > 0 }
      );
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Sincronizzazione con Odoo non riuscita."), "error");
    } finally {
      setIsSyncing(false);
    }
  }

  async function handleDeleteSingle() {
    if (!selected) return;
    const confirmed = window.confirm(`Eliminare l'intervento "${selected.nome ?? "senza nome"}"?`);
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      await interventionsApi.deleteIntervento(selected.id);
      showToast("Intervento eliminato.", "success");
      pannello.chiudi();
      loadRecords();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare l'intervento."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  const columns: TableColumn<Intervento>[] = [
    {
      key: "nome",
      header: "Intervento",
      // Identifica la riga.
      sempreVisibile: true,
      sortValue: (r) => r.nome,
      render: (r) => r.nome ?? "—",
    },
    {
      key: "cliente_nome",
      header: "Cliente",
      // L'altro identificativo, e non è `cliente_id`: è una colonna di testo
      // denormalizzata che la sync scrive da `partner_id[1]`, e `conRelazioni`
      // le preferisce il nome dell'anagrafica solo quando il collegamento
      // esiste. In produzione `cliente_id` è valorizzato su 12 record su 99,
      // ma il nome c'è anche sugli altri: sono due misure diverse.
      sempreVisibile: true,
      sortValue: (r) => r.cliente_nome,
      render: (r) => r.cliente_nome ?? "—",
    },
    {
      key: "tecnico",
      header: "Tecnico",
      // La colonna meglio compilata del modulo: 91 su 99 in produzione.
      sortValue: (r) => r.tecnico,
      render: (r) => r.tecnico ?? "—",
    },
    {
      key: "data_prevista",
      header: "Data prevista",
      // Vuota sull'89% delle righe in produzione (11 su 99), e resta accesa di
      // proposito: è l'unica colonna temporale della vista, e quel vuoto DICE
      // qualcosa — che gli interventi da Odoo arrivano senza data prevista,
      // perché la sync la mette a null sui ticket Helpdesk (vedi il commento
      // sul terzo ramo di `parseMeseRange` in routes/interventions.ts).
      // Nasconderla farebbe sparire l'informazione invece di spiegarla.
      sortValue: (r) => r.data_prevista,
      render: (r) => (r.data_prevista ? new Date(r.data_prevista).toLocaleDateString("it-IT") : "—"),
    },
    {
      key: "data_completamento",
      header: "Data completamento",
      nascostaDiDefault: true,
      sortValue: (r) => r.data_completamento,
      render: (r) =>
        r.data_completamento ? (
          new Date(r.data_completamento).toLocaleDateString("it-IT")
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      key: "sede_nome",
      header: "Sede",
      // 11 su 99: deriva da `sede_id`, che la sync non scrive.
      nascostaDiDefault: true,
      sortValue: (r) => r.sede_nome ?? null,
      render: (r) => r.sede_nome ?? <span className="text-ink/30">—</span>,
    },
    {
      key: "assegnato_nome",
      header: "Assegnato a",
      // 3 su 99: l'assegnazione a persona è appena entrata in uso.
      nascostaDiDefault: true,
      sortValue: (r) => r.assegnato_nome ?? null,
      render: (r) => r.assegnato_nome ?? <span className="text-ink/30">—</span>,
    },
    {
      key: "stato",
      header: "Stato",
      // Porta i badge "In ritardo" e "Senza data": è l'unico avviso in tabella,
      // quindi non si spegne. Si ordina sul solo stato principale — le due
      // segnalazioni si aggiungono, non sostituiscono.
      sempreVisibile: true,
      sortValue: (r) => r.stato,
      render: (r) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.stato ? <Badge stato={r.stato}>{r.stato}</Badge> : "—"}
          {r.in_ritardo && <Badge color="danger">In ritardo</Badge>}
          {/* Un record senza date compare in ogni mese: senza dirlo, sembrerebbe
              programmato proprio nel mese che si sta guardando. */}
          {!r.data_prevista && !r.data_completamento && <Badge color="warning">Senza data</Badge>}
        </div>
      ),
    },
    {
      // La chiave resta `in_garanzia` anche se il valore ora e' `fatturabile`:
      // e' quella salvata nelle preferenze delle colonne nascoste, e cambiarla
      // accenderebbe la colonna a chi l'aveva lasciata spenta.
      key: "in_garanzia",
      header: "Fatturabile",
      // Spenta di default: in produzione `in_garanzia` e' false su tutti e 99
      // gli interventi, perche' fino a ora la casella non esisteva. Quando
      // l'amministrazione comincera' a usarla, si accende.
      //
      // L'intestazione dice "Fatturabile" e non "In garanzia" per restare
      // uguale all'export e al pannello dei filtri: tre nomi diversi per la
      // stessa colonna sarebbero tre cose diverse agli occhi di chi legge.
      //
      // Il valore arriva calcolato dal backend, con la stessa regola di export
      // e report: qui si sceglie solo il colore.
      nascostaDiDefault: true,
      sortValue: (r) => r.fatturabile?.etichetta ?? null,
      render: (r) =>
        r.fatturabile ? (
          <Badge
            color={
              r.fatturabile.valore === "Sì" ? "success" : r.fatturabile.valore === "Non ancora" ? "warning" : "neutral"
            }
          >
            {r.fatturabile.etichetta}
          </Badge>
        ) : (
          <span className="text-ink/30">—</span>
        ),
    },
    {
      key: "odoo_synced",
      header: "Odoo",
      // 84 record su 99 vengono da Odoo: qui la distinzione conta davvero.
      // Si ordina sul solo booleano e non sulla data di sincronizzazione:
      // l'etichetta le mette insieme, ma la domanda che ci si fa guardandola
      // è "quali non sono sincronizzati", non "quale è il più recente".
      sortValue: (r) => (r.odoo_synced ? "Sincronizzato" : "Mai sincronizzato"),
      render: (r) =>
        r.odoo_synced ? (
          <Badge color="success">
            Sincronizzato{r.odoo_last_sync && ` · ${new Date(r.odoo_last_sync).toLocaleDateString("it-IT")}`}
          </Badge>
        ) : (
          <Badge color="neutral">Mai sincronizzato</Badge>
        ),
    },
  ];

  return (
    <div>
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div style={sectionFlatStyle("interventi")} className="flex h-11 w-11 items-center justify-center rounded-xl">
            <Wrench className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">Interventi</h1>
            <p className="mt-1 text-sm text-ink/50">Interventi tecnici post-vendita, sincronizzati da Odoo 19.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            label=""
            placeholder="Cerca titolo, cliente, sede o matricola…"
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            className="min-w-[260px]"
          />
          <MeseSelect value={mese} onChange={setMese} />
          <ExportButton
            endpoint="/interventi/export"
            filename="interventi.xlsx"
            fields={[
              { key: "stato", label: "Stato (testo libero, es. \"Completato\")", type: "text" },
              { key: "data_da", label: "Data prevista da", type: "date" },
              { key: "data_a", label: "Data prevista a", type: "date" },
              // Il pannello parla di "Fatturabile" e non di garanzia: e' la
              // domanda che si fa chi scarica per l'amministrazione. Il filtro
              // lo applica il backend con la stessa regola della colonna; «No»
              // comprende garanzia e annullati.
              { key: "fatturabile", label: "Fatturabile", type: "select", options: ["Sì", "No", "Non ancora"] },
            ]}
          />
          {canImport && (
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              Importa da file
            </Button>
          )}
          {canImport && (
            <Button variant="secondary" onClick={() => setBulkDeleteOpen(true)}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Elimina in blocco
            </Button>
          )}
          {canSync && (
            <Button
              onClick={handleSync}
              loading={isSyncing}
              style={sectionTileStyle("interventi")}
              className="active:translate-y-0 active:shadow-none"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Sincronizza Odoo
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={() => pannello.apriNuovo()}
              style={sectionTileStyle("interventi")}
              className="active:translate-y-0 active:shadow-none"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nuovo intervento
            </Button>
          )}
        </div>
      </header>

      {/* Rimozione selettiva: i due filtri possono coesistere (es. ?ritardo=true&stato=…),
          quindi ciascun banner toglie solo il proprio parametro. */}
      {ritardo && <RitardoBanner onRemove={() => rimuoviParam("ritardo")} />}

      {statoFiltro && (
        <StatoFilterBanner
          stato={etichettaStato}
          colore={coloreFetta(etichettaStato, 0)}
          conteggio={records.length}
          onRemove={() => rimuoviParam("stato")}
        />
      )}

      <Table
        columns={columns}
        data={records}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        emptyMessage={messaggioVuoto}
        onRowClick={(r) => pannello.apri(r.id)}
        ordinabile
        ordine={ordine}
        onOrdineChange={onOrdineChange}
        sceltaColonne="interventi"
      />

      <SlideOver
        open={panelOpen}
        onClose={pannello.chiudi}
        title={selected?.nome ?? "Dettaglio intervento"}
        footer={
          canImport && selected ? (
            <Button type="button" variant="ghost" loading={isDeleting} onClick={handleDeleteSingle}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              Elimina
            </Button>
          ) : undefined
        }
      >
        {selected && (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg bg-primary/5 px-4 py-3 text-sm">
              {selected.richiede_vigilanza && (
                <Badge color="warning" className="mb-2">
                  {/* Con chi, quando si sa: un intervento congiunto senza
                      istituto indicato non lo vede nessun istituto, e dirlo
                      qui è il modo più diretto per accorgersene. */}
                  {selected.congiunto_con
                    ? `Congiunto con ${selected.congiunto_con}`
                    : "Da effettuare congiuntamente alla vigilanza — istituto non indicato"}
                </Badge>
              )}
              <p className="text-ink/70">
                Cliente: <span className="font-semibold text-ink">{selected.cliente_nome ?? "—"}</span>
              </p>
              {/* Sede e indirizzo: erano già salvati alla creazione ma non
                  venivano mai mostrati, tanto da sembrare persi. */}
              <p className="text-ink/70">
                Sede: <span className="font-semibold text-ink">{selected.sede_nome ?? "—"}</span>
              </p>
              {selected.sede_indirizzo && (
                <p className="text-ink/70">
                  Indirizzo: <span className="font-semibold text-ink">{selected.sede_indirizzo}</span>
                </p>
              )}
              <p className="text-ink/70">
                Centro Assistenza:{" "}
                <span className="font-semibold text-ink">{selected.centro_nome ?? "Non assegnato"}</span>
              </p>
              <p className="text-ink/70">
                Tecnico: <span className="font-semibold text-ink">{selected.tecnico ?? "—"}</span>
              </p>
              <p className="text-ink/70">
                Data prevista:{" "}
                <span className="font-semibold text-ink">
                  {selected.data_prevista ? new Date(selected.data_prevista).toLocaleDateString("it-IT") : "—"}
                </span>
              </p>
              <p className="text-ink/70">
                Stato: <span className="font-semibold text-ink">{selected.stato ?? "—"}</span>
              </p>
              {selected.note && (
                <p className="mt-2 text-ink/70">
                  Note: <span className="text-ink">{selected.note}</span>
                </p>
              )}
              <p className="mt-2 text-ink/70">
                Fonte:{" "}
                <span className="font-semibold text-ink">
                  {selected.odoo_model === "helpdesk.ticket"
                    ? "Helpdesk"
                    : selected.odoo_model === "project.task"
                      ? "Assistenza sul campo"
                      : "Locale (non collegato a Odoo)"}
                </span>
              </p>

              {/* I riferimenti d'ordine anche in sola lettura, e non solo nel
                  riquadro di modifica qui sotto: quello compare solo con
                  `interventi:update`, e un admin che apre il pannello per
                  guardare deve vedere i numeri senza dover entrare in modifica.
                  Le righe compaiono solo se c'è qualcosa da dire — un pannello
                  con due trattini in più non informa nessuno. */}
              {isAdminOrStaff && (selected.numero_oda || selected.numero_odv) && (
                <div className="mt-2 border-t border-black/5 pt-2">
                  {selected.numero_oda && (
                    <p className="text-ink/70">
                      OdA: <span className="font-semibold text-ink">{selected.numero_oda}</span>{" "}
                      <span className="text-ink/50">
                        ({selected.oda_fattura_ricevuta ? "fattura fornitore ricevuta" : "fattura fornitore attesa"})
                      </span>
                    </p>
                  )}
                  {selected.numero_odv && (
                    <p className="text-ink/70">
                      OdV: <span className="font-semibold text-ink">{selected.numero_odv}</span>{" "}
                      <span className="text-ink/50">
                        {/* La sezione del Report Ordini, calcolata dal backend con la
                            stessa funzione del report: qui solo il testo, così il
                            pannello non può dire «da fatturare» dove il report dice
                            altro (per esempio su un intervento annullato). */}
                        ({testoSezioneOdv(selected.sezione_odv)})
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {canUpdate && (
              <div className="border-t border-line pt-4">
                <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Aggiorna intervento</p>
                <InterventoModifica
                  intervento={selected}
                  // La riassegnazione è riservata ad admin/staff: senza elenco
                  // il componente non mostra affatto il campo, coerente col
                  // backend che per gli altri ignora centro_assistenza_id.
                  centri={isAdminOrStaff ? centri : undefined}
                  onAggiornato={(record) => {
                    // Il pannello resta aperto sul record aggiornato e la
                    // lista dietro si riallinea, così stato e date non
                    // restano indietro nella tabella.
                    setSelected(record);
                    loadRecords();
                  }}
                />
              </div>
            )}

            {selected.dispositivo_catalogo_id && <DispositivoMateriale dispositivoId={selected.dispositivo_catalogo_id} />}

            {!isReadOnly && selected.odoo_model && selected.odoo_id && <NotaOdooSection interventoId={selected.id} />}

            {!isReadOnly && (
              <div className="border-t border-line pt-4">
                <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Rapportino R.I.T.</p>
                <RapportinoRIT
                  entityType="intervento"
                  entityId={selected.id}
                  defaultClienteNome={selected.cliente_nome}
                  // L'indirizzo del rapportino deriva dalla sede già scelta
                  // sull'intervento, invece di essere ridigitato a mano.
                  defaultIndirizzo={selected.sede_indirizzo}
                />
              </div>
            )}

            {!isReadOnly && (
              <div className="border-t border-line pt-4">
                <p className="mb-3 text-sm font-bold uppercase tracking-wide text-ink/50">Foto e video</p>
                <MediaUploader entityType="intervento" entityId={selected.id} />
              </div>
            )}
          </div>
        )}
      </SlideOver>

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => loadRecords()}
        entityType="interventi"
        title="Importa interventi da file"
      />

      <BulkDeleteModal open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} onDeleted={() => loadRecords()} />

      <CreateInterventoModal
        open={createOpen}
        onClose={pannello.chiudi}
        onCreated={() => loadRecords()}
        centri={centri}
        isAdminOrStaff={isAdminOrStaff}
      />
    </div>
  );
}

/**
 * Scrive una nota sul record Odoo collegato (chatter/log note standard di
 * Odoo) — non sposta lo stage. Visibile solo per interventi già collegati a
 * un ticket/task Odoo (odoo_model + odoo_id valorizzati).
 */
function NotaOdooSection({ interventoId }: { interventoId: string }) {
  const { showToast } = useToast();
  const [messaggio, setMessaggio] = useState("");
  const [isSending, setIsSending] = useState(false);

  async function handleSend() {
    if (!messaggio.trim()) return;
    setIsSending(true);
    try {
      await interventionsApi.inviaNotaOdoo(interventoId, messaggio.trim());
      showToast("Nota inviata su Odoo.", "success");
      setMessaggio("");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile inviare la nota."), "error");
    } finally {
      setIsSending(false);
    }
  }

  return (
    <div className="border-t border-line pt-4">
      <p className="mb-2 text-sm font-bold uppercase tracking-wide text-ink/50">Invia nota a Odoo</p>
      <textarea
        rows={2}
        value={messaggio}
        onChange={(e) => setMessaggio(e.target.value)}
        placeholder="Scrivi una nota che apparirà nel chatter del ticket/task su Odoo…"
        className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
      />
      <div className="mt-2 flex justify-end">
        <Button type="button" size="sm" loading={isSending} disabled={!messaggio.trim()} onClick={handleSend}>
          Invia
        </Button>
      </div>
    </div>
  );
}

/**
 * Eliminazione in blocco per filtro — mai a scatola chiusa: prima mostra
 * quanti record corrisponderebbero (senza cancellare nulla), solo dopo
 * conferma esplicita esegue l'eliminazione vera. Nato dal caso reale di 266
 * project.task Odoo generici, non legati all'assistenza, importati per
 * sbaglio prima che la sync fosse filtrata per progetto.
 */
function BulkDeleteModal({ open, onClose, onDeleted }: { open: boolean; onClose: () => void; onDeleted: () => void }) {
  const { showToast } = useToast();
  const [stato, setStato] = useState("");
  const [odooModel, setOdooModel] = useState<"" | "helpdesk.ticket" | "project.task">("");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [preview, setPreview] = useState<number | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  function reset() {
    setStato("");
    setOdooModel("");
    setDataDa("");
    setDataA("");
    setPreview(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const filters = {
    stato: stato || undefined,
    odoo_model: odooModel || undefined,
    data_da: dataDa || undefined,
    data_a: dataA || undefined,
  };
  const hasAnyFilter = Boolean(stato || odooModel || dataDa || dataA);

  async function handleCheck() {
    setIsChecking(true);
    setPreview(null);
    try {
      const { count } = await interventionsApi.bulkDeletePreview(filters);
      setPreview(count);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile calcolare l'anteprima."), "error");
    } finally {
      setIsChecking(false);
    }
  }

  async function handleConfirmDelete() {
    if (preview === null) return;
    const confirmed = window.confirm(
      `Stai per eliminare DEFINITIVAMENTE ${preview} interventi. L'operazione non è reversibile. Continuare?`
    );
    if (!confirmed) return;
    setIsDeleting(true);
    try {
      const { deleted } = await interventionsApi.bulkDeleteInterventi(filters);
      showToast(`${deleted} interventi eliminati.`, "success");
      handleClose();
      onDeleted();
    } catch (err) {
      showToast(getErrorMessage(err, "Eliminazione non riuscita."), "error");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title="Elimina interventi in blocco" size="sm">
      <div className="flex flex-col gap-4">
        <p className="text-xs text-ink/50">
          Imposta almeno un filtro, poi controlla quanti interventi corrisponderebbero prima di eliminarli — nessuna
          eliminazione avviene finché non confermi esplicitamente il numero mostrato.
        </p>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Stato (testo libero, es. "Da programmare")</label>
          <input
            type="text"
            value={stato}
            onChange={(e) => {
              setStato(e.target.value);
              setPreview(null);
            }}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Fonte</label>
          <select
            value={odooModel}
            onChange={(e) => {
              setOdooModel(e.target.value as typeof odooModel);
              setPreview(null);
            }}
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">Tutte</option>
            <option value="project.task">Assistenza sul campo</option>
            <option value="helpdesk.ticket">Helpdesk</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Data prevista da</label>
            <input
              type="date"
              value={dataDa}
              onChange={(e) => {
                setDataDa(e.target.value);
                setPreview(null);
              }}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink">Data prevista a</label>
            <input
              type="date"
              value={dataA}
              onChange={(e) => {
                setDataA(e.target.value);
                setPreview(null);
              }}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            />
          </div>
        </div>

        {preview !== null && (
          <div className="rounded-lg bg-accent/10 px-3 py-2 text-sm text-ink">
            <strong>{preview}</strong> {preview === 1 ? "intervento corrisponde" : "interventi corrispondono"} a
            questi filtri.
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="ghost" onClick={handleClose}>
            Annulla
          </Button>
          {preview === null ? (
            <Button type="button" loading={isChecking} disabled={!hasAnyFilter} onClick={handleCheck}>
              Controlla quanti sono
            </Button>
          ) : (
            <Button type="button" loading={isDeleting} disabled={preview === 0} onClick={handleConfirmDelete}>
              Elimina {preview} interventi
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Creazione manuale — non passa da Odoo. Pensata soprattutto per admin/staff
 * che precompilano l'anagrafica cliente (con suggerimenti da quelli già
 * presenti) e assegnano l'intervento a un Centro Assistenza specifico, che
 * lo troverà pronto nella propria lista invece di doverlo creare da zero.
 */
function CreateInterventoModal({
  open,
  onClose,
  onCreated,
  centri,
  isAdminOrStaff,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  centri: CentroAssistenzaOption[];
  isAdminOrStaff: boolean;
}) {
  const { showToast } = useToast();
  const [nome, setNome] = useState("");
  const [clienteNome, setClienteNome] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [sede, setSede] = useState("");
  const [sedeId, setSedeId] = useState("");
  // Collegamento "Collegato a installazione" (installation_schedule, fonte reale)
  // — stesso pattern delle Attivazioni. Opzionale.
  const [scheduleId, setScheduleId] = useState("");
  const [schedaLabel, setSchedaLabel] = useState("");
  const [dataPrevista, setDataPrevista] = useState("");
  // L'orario dell'appuntamento accompagna la data PREVISTA, non quella di
  // completamento: e' quando ci si e' dati appuntamento, non quando si e' finito.
  const [orarioInizio, setOrarioInizio] = useState("");
  const [orarioFine, setOrarioFine] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [note, setNote] = useState("");
  // Dispositivo (tipicamente la cassaforte) a cui si riferisce l'intervento —
  // opzionale, serve solo a consultare subito la documentazione collegata.
  const [dispositivoModello, setDispositivoModello] = useState("");
  const [dispositivoId, setDispositivoId] = useState("");
  // Il PEZZO fisico, distinto dal modello a catalogo qui sopra: quello serve
  // ai manuali, questo e' la cassaforte con la sua matricola.
  const [deviceTesto, setDeviceTesto] = useState("");
  const [deviceId, setDeviceId] = useState("");
  // Un intervento si assegna solo a un Centro Assistenza — mai a un Istituto
  // di Vigilanza, che prende in carico solo attivazioni (casseforti) e
  // sopralluoghi, non interventi tecnici.
  const [assegnaCentro, setAssegnaCentro] = useState("");
  // Tag informativo, non un'assegnazione — l'intervento resta del Centro
  // Assistenza, indica solo che va coordinato con la vigilanza a parte.
  const [richiedeVigilanza, setRichiedeVigilanza] = useState(false);
  // Con QUALE istituto: "" | uuid | ALTRO, più il nome scritto a mano nel caso
  // "Altro". Due stati e non uno perché la tendina deve poter stare su "Altro"
  // prima che il nome sia stato scritto.
  const [istitutoScelta, setIstitutoScelta] = useState("");
  const [istitutoAltro, setIstitutoAltro] = useState("");
  // I cinque riferimenti OdA/OdV. Compilabili già alla creazione, non solo dopo:
  // un intervento creato da admin per un centro nasce spesso proprio dall'OdA
  // appena emesso, e obbligare a salvare e riaprire per trascriverne il numero
  // è il modo sicuro di non trascriverlo mai.
  const puoGestireOrdini = usaPuoGestireRiferimentiOdoo();
  const [riferimenti, setRiferimenti] = useState<ValoriRiferimentiOdoo>({ ...RIFERIMENTI_ODOO_VUOTI });
  const [isSaving, setIsSaving] = useState(false);

  function reset() {
    setNome("");
    setClienteNome("");
    setClienteId("");
    setSede("");
    setSedeId("");
    setScheduleId("");
    setSchedaLabel("");
    setDataPrevista("");
    setOrarioInizio("");
    setOrarioFine("");
    setTecnico("");
    setNote("");
    setDispositivoModello("");
    setDispositivoId("");
    setDeviceTesto("");
    setDeviceId("");
    setAssegnaCentro("");
    setRichiedeVigilanza(false);
    setIstitutoScelta("");
    setIstitutoAltro("");
    setRiferimenti({ ...RIFERIMENTI_ODOO_VUOTI });
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setIsSaving(true);
    try {
      await interventionsApi.createIntervento({
        nome: nome || null,
        cliente_nome: clienteNome || null,
        cliente_id: clienteId || null,
        sede_id: sedeId || null,
        schedule_id: scheduleId || null,
        data_prevista: dataPrevista || null,
        orario_inizio: orarioInizio || null,
        orario_fine: orarioFine || null,
        tecnico: tecnico || null,
        note: note || null,
        dispositivo_catalogo_id: dispositivoId || null,
        // Una scelta esplicita vince sull'eredita' da `schedule_id`: la
        // precedenza sta nel backend, qui si manda solo cio' che l'utente ha
        // davvero scelto.
        device_id: deviceId || null,
        centro_assistenza_id: isAdminOrStaff ? assegnaCentro || null : undefined,
        // Le tre colonne della vigilanza congiunta si compongono in un posto
        // solo, condiviso col form di modifica.
        ...payloadVigilanza(richiedeVigilanza, istitutoScelta, istitutoAltro),
        // Idem per i cinque riferimenti OdA/OdV: stessa funzione del pannello di
        // modifica, così i due form non possono divergere.
        ...(puoGestireOrdini ? payloadRiferimentiOdoo(riferimenti, RIFERIMENTI_ODOO_VUOTI) : {}),
      });
      showToast("Intervento creato.", "success");
      handleClose();
      onCreated();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile creare l'intervento."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Nuovo intervento"
      size="sm"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={handleClose}>
            Annulla
          </Button>
          <Button type="button" loading={isSaving} onClick={handleSubmit}>
            Crea
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Nome intervento" value={nome} onChange={(e) => setNome(e.target.value)} />

        <ScheduleAutocomplete
          label="Collegato a installazione (opzionale)"
          displayValue={schedaLabel}
          onSelect={(s) => {
            setScheduleId(s.id);
            setSchedaLabel(`${s.insegna ?? "—"} · ${s.localita ?? "—"}`);
            // Precompila cliente/sede/dispositivo dalla scheda scelta — come su Attivazioni.
            if (s.cliente_id) setClienteId(s.cliente_id);
            setClienteNome(s.cliente_nome ?? s.insegna ?? "");
            if (s.localita) setSede(s.localita);
            if (s.sede_id) setSedeId(s.sede_id);
            if (s.dispositivo_catalogo_id) {
              setDispositivoId(s.dispositivo_catalogo_id);
              setDispositivoModello([s.dispositivo_marca, s.dispositivo_modello].filter(Boolean).join(" "));
            }
          }}
          onClear={() => {
            setScheduleId("");
            setSchedaLabel("");
          }}
        />

        <ClienteAutocomplete
          label="Cliente"
          value={clienteNome}
          onChange={setClienteNome}
          onSelect={(c) => setClienteId(c.id)}
        />

        <SedeAutocomplete
          label="Sede"
          value={sede}
          onChange={setSede}
          sedeId={sedeId}
          onSedeIdChange={setSedeId}
          soloSelezione
          onSelect={(s) => {
            // Scegliendo una sede già esistente, l'azienda madre segue —
            // stesso motivo di Installazioni: una sede spostata sotto
            // un'altra azienda si riflette qui senza doverlo ripetere a mano.
            setClienteId(s.cliente_id);
            setClienteNome(s.cliente_nome);
          }}
        />

        <DeviceFisicoAutocomplete
          label="Cassaforte"
          value={deviceTesto}
          onChange={setDeviceTesto}
          deviceId={deviceId}
          onDeviceIdChange={setDeviceId}
          sedeId={sedeId || null}
          hint={
            sedeId
              ? "I pezzi che stanno in questa sede compaiono per primi."
              : "Scegli prima la sede per vedere subito la cassaforte che sta lì."
          }
        />

        <Input label="Data prevista" type="date" value={dataPrevista} onChange={(e) => setDataPrevista(e.target.value)} />
        <CampiOrario
          inizio={{ value: orarioInizio, onChange: (e) => setOrarioInizio(e.target.value) }}
          fine={{ value: orarioFine, onChange: (e) => setOrarioFine(e.target.value) }}
        />
        <Input label="Tecnico" value={tecnico} onChange={(e) => setTecnico(e.target.value)} />

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">Note</label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
          />
        </div>

        <DispositivoAutocomplete
          label="Dispositivo interessato (opzionale)"
          placeholder="Es. YOUGO UB2500SI"
          value={dispositivoModello}
          onChange={setDispositivoModello}
          onSelect={(d) => {
            setDispositivoModello(`${d.marca} ${d.modello}`);
            setDispositivoId(d.id);
          }}
        />
        {dispositivoId && <DispositivoMateriale dispositivoId={dispositivoId} />}

        <VigilanzaCongiunta
          richiede={richiedeVigilanza}
          onRichiedeChange={setRichiedeVigilanza}
          scelta={istitutoScelta}
          onSceltaChange={setIstitutoScelta}
          altro={istitutoAltro}
          onAltroChange={setIstitutoAltro}
        />

        {puoGestireOrdini && (
          <RiferimentiOdoo idPrefisso="intervento-nuovo" valori={riferimenti} onChange={setRiferimenti} />
        )}

        {isAdminOrStaff && (
          <div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-accent/40 bg-accent/5 p-3">
            <label className="text-sm font-medium text-ink">Assegna a Centro Assistenza (opzionale)</label>
            <select
              value={assegnaCentro}
              onChange={(e) => setAssegnaCentro(e.target.value)}
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            >
              <option value="">— Nessuno (resta locale) —</option>
              {centri.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
            <p className="text-xs text-ink/50">
              Precompila l'anagrafica e assegna direttamente al centro scelto — comparirà nella sua lista pronto da
              completare.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}
